import React, { useState } from 'react';
import { updateStatus } from './lib/updateSchedule';

// Admin-only red banner: AI Visibility data must be refreshed every three months
// or it goes out of date. Shows when the property was last updated and when the
// next update is due, and escalates its message once that date arrives or passes.
// The page renders it for admins only; it takes the property's full run list
// (not the date-range-filtered one) so the schedule doesn't move with the picker.

/** "2026-12-21" → "December 21, 2026" (local calendar day, no UTC shift). */
const fmtDay = (ymd) => new Date(`${ymd}T00:00:00`).toLocaleDateString('en-US', {
  month: 'long', day: 'numeric', year: 'numeric',
});

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The banner sentence for each schedule state. */
const messageFor = ({ state, lastRun, dueDate, days }) => {
  switch (state) {
    case 'none':
      return 'No update yet. Run AI Visibility to get data.';
    case 'overdue':
      return `Was due ${fmtDay(dueDate)} (${plural(-days, 'day')} overdue). Last updated ${fmtDay(lastRun)}.`;
    case 'due':
      return `Next due today, ${fmtDay(dueDate)}. Last updated ${fmtDay(lastRun)}.`;
    case 'upcoming':
    default:
      return `Next due ${fmtDay(dueDate)} (${plural(days, 'day')}). Last updated ${fmtDay(lastRun)}.`;
  }
};

// Closing hides the banner for the rest of this browser session only — it comes
// back on the next visit so the update can't be dismissed and forgotten. The key
// carries the state and due date, so it also comes back as soon as the situation
// changes (upcoming → due → overdue, or a new run moves the due date).
const dismissKey = (clientId, { state, dueDate }) => `aim-update-warning:${clientId || 'none'}:${state}:${dueDate || 'none'}`;

const readDismissed = (key) => {
  try { return window.sessionStorage.getItem(key) === '1'; } catch { return false; }
};

const writeDismissed = (key) => {
  try { window.sessionStorage.setItem(key, '1'); } catch { /* storage blocked — close for this render only */ }
};

export default function AimUpdateWarning({ runs, clientId, now }) {
  const status = updateStatus(runs, now);
  const key = dismissKey(clientId, status);
  const [closedKey, setClosedKey] = useState(null);
  if (closedKey === key || readDismissed(key)) return null;

  const close = () => { writeDismissed(key); setClosedKey(key); };

  return (
    <div
      data-testid="aim-update-warning"
      data-state={status.state}
      role={status.state === 'upcoming' ? 'status' : 'alert'}
      className="flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
    >
      <svg className="mt-0.5 h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
      <p className="min-w-0 flex-1 text-sm">
        <strong className="font-semibold">Update AI Visibility quarterly.</strong>{' '}
        {messageFor(status)}
      </p>
      <button
        type="button"
        onClick={close}
        aria-label="Close update warning"
        className="-m-1 shrink-0 rounded-md p-1 text-red-700 transition-colors hover:bg-red-100 hover:text-red-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 dark:text-red-300 dark:hover:bg-red-900/40 dark:hover:text-red-100"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
